import React, { useState, useEffect, useCallback } from 'react';
import { UserId } from 'coop-adventure-engine';
import friendsService from '../../services/friendsService';
import { FriendCard } from './FriendCard';

export interface FriendsListProps {
  /**
   * Optional target scene for clickable friends
   */
  selectionTarget?: string;

  /**
   * Optional handler for friend selection
   * When selectionTarget is provided, receives (friendId, targetScene)
   * Otherwise, receives just (friendId)
   */
  onFriendSelect?: (friendId: UserId, targetScene?: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom styles
   */
  style?: React.CSSProperties;
}

/**
 * Component for displaying list of friends
 */
export const FriendsList: React.FC<FriendsListProps> = ({
  selectionTarget,
  onFriendSelect,
  className = '',
  style
}) => {
  const [friends, setFriends] = useState<UserId[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFriends = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const user = await friendsService.getFriends();
      setFriends(user.friends || []);
    } catch (err) {
      console.error('Failed to load friends:', err);
      setError(err instanceof Error ? err.message : 'Failed to load friends');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFriends();

    // Listen for friend updates
    const handleFriendUpdate = () => {
      loadFriends();
    };

    window.addEventListener('friend:update', handleFriendUpdate);
    return () => {
      window.removeEventListener('friend:update', handleFriendUpdate);
    };
  }, [loadFriends]);

  const handleFriendClick = useCallback(
    (friendId: UserId) => {
      if (selectionTarget && onFriendSelect) {
        onFriendSelect(friendId, selectionTarget);
      } else if (onFriendSelect) {
        onFriendSelect(friendId);
      }
    },
    [selectionTarget, onFriendSelect]
  );

  if (isLoading) {
    return (
      <div
        className={`friends-list loading ${className}`}
        style={style}
        data-testid="friends-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic text-sm">Loading friends...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`friends-list error ${className}`}
        style={style}
        data-testid="friends-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (friends.length === 0) {
    return (
      <div
        className={`friends-list empty ${className}`}
        style={style}
        data-testid="friends-list-empty"
      >
        <div className="flex items-center justify-center py-2" data-testid="empty-friends">
          <p className="italic text-center text-gray-400">No friends yet</p>
        </div>
      </div>
    );
  }

  // If selectionTarget is provided, render as clickable inline options (similar to FriendRequestList)
  if (selectionTarget) {
    return (
      <div
        className={`friends-list selectable ${className}`}
        style={style}
        data-testid="friends-list-selectable"
      >
        <div
          className="friends-list-items flex flex-col"
          style={{ gap: '0.5rem' }}
          data-testid="friends-items-list"
        >
          {friends.map((friendId) => (
            <button
              key={friendId}
              onClick={() => handleFriendClick(friendId)}
              className="friend-item-button inventory-item-button w-full"
              data-testid={`friend-${friendId}`}
              data-friend-id={friendId}
              aria-label={`Select friend ${friendId}`}
            >
              <FriendCard
                friendId={friendId}
                noContainer={true}
              />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`friends-list ${className}`}
      style={style}
      data-testid="friends-list"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {friends.map((friendId) => (
          <FriendCard
            key={friendId}
            friendId={friendId}
            {...(onFriendSelect && { onClick: () => handleFriendClick(friendId) })}
          />
        ))}
      </div>
    </div>
  );
};

export default FriendsList;

